//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { LocalDictionaryFactory } from '../../api/api.module'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { HistorySource } from '../../common/history/history.directive.controller'
import { LoggerService } from '../../common/common.module'

import { ConfigHelper } from '../../../helpers/config-helper'
import { DataTypeGroupComponent } from '../../../helpers/data-type-group-component'
import { EventsNames } from '../../../helpers/events-names'
import { IUserSession } from '../../../helpers/user-session'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { TemplateHelper } from '../../../helpers/template-helper'
import { OperatorLayoutSettings } from './../../../helpers/operator-layout-settings'

import { Activity } from '../../../models/common/activity'
import { Enums } from '../../../models/common/enums'
import { OpDataTypeInGroup } from '../../../models/core/op-data-type-in-group'
import { OpReport } from '../../../models/dw/op-report'
import { SESSION } from '../../../models/custom/session'

import { ReportHelper } from '../reports.helper'
import { OpHistory } from '../../../models/custom/op-history'
import { OpFile } from '../../../models/core/op-file';
import { FileComponent } from '../../../helpers/file-component';
import { CommonHelper } from '../../../helpers/common-helper';

export interface ReportDetailsScope extends DetailsScope {
    REPORT_PARAMS_ACCORDION_NAME: string
    REPORT_COMMON_TAB_NAME: string
    REPORT_HISTORY_TAB_NAME: string 

    id: number
    
    detailsParametersOptions: DetailsParametersOptions
    reportHistoryOptions: HistorySource

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class ReportDetailsDirectiveController extends Details<ReportDetailsScope> {

    // Consts

    private get REPORT_PARAMS_ACCORDION_NAME() { return 'REPORT_PARAMS_ACCORDION_NAME' }
    private get REPORT_COMMON_TAB_NAME() { return 'REPORT_COMMON_TAB_NAME' } 
    private get REPORT_HISTORY_TAB_NAME() { return 'REPORT_HISTORY_TAB_NAME' } 

    // Consts End
    

    // Members

    private referenceObject: OpReport
    private referenceObjectId: number

    // Members End


    constructor(
        $scope: ReportDetailsScope, 
        $q: ng.IQService,
        $timeout: ng.ITimeoutService, 
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory, 
        userSession: IUserSession, 
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.ReportPopupTabActive, userSession, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.REPORTS_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    
    // Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.setHistoryOptions()

        this.onClear()
        this.showPopup()
        this.onRefresh() 
    }
    
    private onRefresh(toRefresh: OpReport = null): void {
        const deferred: JQueryDeferred<OpReport> = $.Deferred<OpReport>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider().Report
                .getReport(this.referenceObjectId)
                .then((result: OpReport) => {
                    this.$scope.popupTitle = result.toString()

                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    let errorMsg: string = error

                    if (SESSION.isType(error)) {
                        errorMsg = (<SESSION>error).toString()
                    }
                    
                    this.logger.log(errorMsg)
                    deferred.resolve(null)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpReport) => {
                if (result && (!this.referenceObject || this.referenceObject.idReport == result.idReport)) {
                    this.referenceObject = result
                    
                    DataTypeGroupComponent
                        .getDataTypeInGroupByModule(
                            this.userSession.getDataProvider(),
                            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY, 
                            this.moduleName
                        )
                        .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                            this.$scope.detailsParametersOptions = {
                                dataTypes: dataTypeInGroup,
                                referenceType: this.referenceType,
                                referenceObject: this.referenceObject,
                                useDynamicValues: true
                            }
                        })
                }
            })
    }

    private onParamsEdit(): void {
        const createEditInitParams: CreateEditInitParams = {
            moduleName: this.moduleName,
            idReferenceType: this.referenceType,
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            referenceObjectId: this.referenceObject.idReport,
            referenceObject: this.referenceObject,
            sections: null,
            useBusinessObjects: true
        }

        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.referenceObject = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        ReportHelper
            .deleteReport(this.userSession.getDataProvider(), this.referenceObjectId, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))

                this.onClose()
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onReportGenerate(): void {
        this.$scope.$root.$broadcast(EventsNames.REPORT_GENERATE, this.referenceObject)
    }

    // Events End


    // Setup

    getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ReportParameters)) {
            items.push({
                name: this.REPORT_PARAMS_ACCORDION_NAME,
                showInTab: this.REPORT_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_EDIT) 
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ReportParameters),

                editMethod: () => this.onParamsEdit(),
            })
            
            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ReportDetailsAccordions, 0, items))
        }

        return accordions
    }
    
    // Saving Report Files

    private saveReportFile(history: OpHistory): void {
        if (this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_GENERATED_LIST) && history != null) {
            this.userSession
                .getDataProvider()
                .Report
                .getGeneratedReport(this.referenceObjectId, history.descr)
                .then((file: OpFile) => {
                    if (file != null) {
                        FileComponent.OpenFileSaver(file)
                    }
                })
                .fail((error: any) => {
                    let errorMsg: string = error

                    if (SESSION.isType(error)) {
                        errorMsg = (<SESSION>error).toString()
                        CommonHelper.openInfoPopup(errorMsg, 'error', 4000)
                    } else {
                        CommonHelper.openInfoPopup(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, this.moduleName), 'error', 4000)
                    }
                })
        }
    }

    // Saving Report Files End


    private getActions(): DevExpress.ui.dxButtonOptions[] {
        const actions: DevExpress.ui.dxButtonOptions[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_GENERATE_OP)) {
            actions.push({
                template: TemplateHelper.getIconWithText(
                    LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__GENERATE, this.moduleName),
                    ConfigHelper.Icon.REPORT_GENERATE,
                    this.userSession
                ),
                width: '100%',

                onClick: (e: any) => this.onReportGenerate(),
            })
        }

        return actions
    }

    getTabs(): any[] {
        const tabs: any = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ReportMain)
            && !this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ReportParameters)) {
                tabs.push({ 
                    template: TemplateHelper.getContextMenuItem(
                        LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                        ConfigHelper.Icon.HOME,
                        this.userSession
                    ),
                    name: this.REPORT_COMMON_TAB_NAME
                })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ReportHistory)) {
            tabs.push({ 
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History),
                    ConfigHelper.Icon.HISTORY,
                    this.userSession
                ),
                name: this.REPORT_HISTORY_TAB_NAME 
            })
        }

        return tabs
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id, 
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id, 
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,
            actions: this.getActions(),
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.REPORT_DEL) ? Activity[Activity.REPORT_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setHistoryOptions(): void {
        this.$scope.reportHistoryOptions = {
            dataMethodName: null, // DO USUNIĘCIA
            id: null, // DO USUNIĘCIA
            name: 'reportHistory',
            referenceType: this.referenceType,
            referenceValue: this.referenceObjectId,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ReportHistory),

            onRowDoubleClick: (history: OpHistory) => this.saveReportFile(history)
        }
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setTemplateConsts(): void {
        this.$scope.REPORT_COMMON_TAB_NAME = this.REPORT_COMMON_TAB_NAME
        this.$scope.REPORT_HISTORY_TAB_NAME = this.REPORT_HISTORY_TAB_NAME
        this.$scope.REPORT_PARAMS_ACCORDION_NAME = this.REPORT_PARAMS_ACCORDION_NAME
    }

    // Setup End

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}